import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import AdminLogin from './AdminLogin';
import { formatParisDateTime, formatParisTime, dateToKey } from './utils/parisTime';
import { API_URL, SOCKET_URL, adminFetch, getAdminToken, clearAdminToken } from './utils/api';
import './Admin.css';

registerLocale('fr', fr);

// ---- Helpers de regroupement par service ----

const SERVICE_META = {
  lunch: { label: 'Midi', emoji: '🌅', startHour: 12, endHour: 15 },
  dinner: { label: 'Soir', emoji: '🌙', startHour: 19, endHour: 23.99 }
};

const dateKeyOf = (value) => String(value).slice(0, 10);

const serviceStart = (dateKey, service) =>
  new Date(`${dateKey}T${String(SERVICE_META[service].startHour).padStart(2, '0')}:00:00`);

const serviceEnd = (dateKey, service) => {
  const end = new Date(`${dateKey}T00:00:00`);
  end.setMinutes(SERVICE_META[service].endHour * 60);
  return end;
};

const localKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const todayKey = () => localKey(new Date());
const tomorrowKey = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return localKey(d);
};

const formatDayLabel = (dateKey) => {
  if (dateKey === todayKey()) return "Aujourd'hui";
  if (dateKey === tomorrowKey()) return 'Demain';
  return new Date(`${dateKey}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long'
  });
};

const groupReservationsByService = (reservations) => {
  const groups = new Map();

  reservations.forEach(r => {
    const dateKey = dateKeyOf(r.date);
    const key = `${dateKey}|${r.service}`;
    if (!groups.has(key)) {
      groups.set(key, { dateKey, service: r.service, reservations: [], guests: 0 });
    }
    const group = groups.get(key);
    group.reservations.push(r);
    group.guests += r.guests;
  });

  const sorted = [...groups.values()].sort(
    (a, b) => serviceStart(a.dateKey, a.service) - serviceStart(b.dateKey, b.service)
  );

  sorted.forEach(g => {
    g.reservations.sort((a, b) => (a.time || '99:99').localeCompare(b.time || '99:99'));
  });

  const now = new Date();
  return {
    upcoming: sorted.filter(g => serviceEnd(g.dateKey, g.service) >= now),
    past: sorted.filter(g => serviceEnd(g.dateKey, g.service) < now)
  };
};

const isServiceNow = (dateKey, service) => {
  const now = new Date();
  const start = serviceStart(dateKey, service);
  start.setMinutes(start.getMinutes() - 60);
  return now >= start && now <= serviceEnd(dateKey, service);
};

const groupOrdersByDay = (orders) => {
  const parisDay = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit'
  });

  const groups = new Map();
  orders.forEach(o => {
    const dateKey = parisDay.format(new Date(o.pickupTime));
    if (!groups.has(dateKey)) groups.set(dateKey, { dateKey, orders: [] });
    groups.get(dateKey).orders.push(o);
  });

  const sorted = [...groups.values()].sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  sorted.forEach(g => g.orders.sort((a, b) => new Date(a.pickupTime) - new Date(b.pickupTime)));
  return sorted;
};

const minutesUntil = (value) => Math.round((new Date(value) - new Date()) / 60000);

const STATUS_BADGES = {
  pending: { text: 'En attente', class: 'badge-attente' },
  preparing: { text: 'En préparation', class: 'badge-prepa' },
  ready: { text: 'Prêt', class: 'badge-pret' },
  completed: { text: 'Récupéré', class: 'badge-fini' },
  cancelled: { text: 'Annulé', class: 'badge-annule' },
  confirmed: { text: 'Confirmée', class: 'badge-confirme' }
};

const DAY_LABELS = {
  monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi', thursday: 'Jeudi',
  friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
};

const formatPhoneFR = (value) =>
  (value || '').replace(/\D/g, '').slice(0, 10).match(/.{1,2}/g)?.join(' ') || '';

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(getAdminToken()));
  const [activeTab, setActiveTab] = useState('orders');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [completedReservations, setCompletedReservations] = useState([]);
  const [timeSlots, setTimeSlots] = useState({});
  const [tablesConfig, setTablesConfig] = useState({ totalTables: 0, tables: [] });
  const [capacityOverrides, setCapacityOverrides] = useState([]);
  const [showPastServices, setShowPastServices] = useState(false);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [loyaltyCustomers, setLoyaltyCustomers] = useState([]);
  const [loyaltyEvents, setLoyaltyEvents] = useState([]);
  const [loyaltySaved, setLoyaltySaved] = useState(false);
  const [eventForm, setEventForm] = useState({
    name: '', multiplier: 2, startDate: null, endDate: null, service: 'all'
  });
  const [newOverride, setNewOverride] = useState({
    date: null, service: 'both', capacity: 30, note: ''
  });
  const [tableTypes, setTableTypes] = useState({ types: [], totalSeats: 0, totalTables: 0 });
  const [typeForm, setTypeForm] = useState({ count: 1, seats: 2, modulable: true, label: '' });
  const [servicePlans, setServicePlans] = useState({});
  const [openPlans, setOpenPlans] = useState({});
  const [stats, setStats] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('month');
  const [closures, setClosures] = useState([]);
  const [closureForm, setClosureForm] = useState({ startDate: null, endDate: null, scope: 'all', note: '' });
  const [appSettings, setAppSettings] = useState({ maxOrdersPerSlot: 0 });
  const [pushStatus, setPushStatus] = useState(null);
  const [pushThisDevice, setPushThisDevice] = useState(false);
  const [managedCustomerId, setManagedCustomerId] = useState(null);
  const [custEdit, setCustEdit] = useState(null);
  const [pointsAdjust, setPointsAdjust] = useState({ points: '', label: '' });
  const [custMsg, setCustMsg] = useState('');
  const [productForm, setProductForm] = useState({
    name: '', price: '', category: '', description: '', points_price: ''
  });
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editProductForm, setEditProductForm] = useState(null);
  const [diningTables, setDiningTables] = useState([]);
  const [newTableName, setNewTableName] = useState('');
  // Édition / ajout de réservations
  const [resaEditId, setResaEditId] = useState(null);
  const [resaForm, setResaForm] = useState(null);
  const [showAddResa, setShowAddResa] = useState(false);
  const [showResaDetails, setShowResaDetails] = useState(false);
  const [resaWarning, setResaWarning] = useState('');
  // Édition d'une commande à emporter
  const [orderEditId, setOrderEditId] = useState(null);
  const [orderEditItems, setOrderEditItems] = useState([]);
  const [orderEditTime, setOrderEditTime] = useState('');
  const [orderEditPickerCat, setOrderEditPickerCat] = useState('');
  // Historique paginé
  const [histType, setHistType] = useState('orders');
  const [histData, setHistData] = useState({ rows: [], total: 0, page: 1, totalPages: 1 });
  const [histFilters, setHistFilters] = useState({ q: '', status: 'all', channelOrService: 'all', from: '', to: '' });
  const [histPage, setHistPage] = useState(1);
  const [histLoading, setHistLoading] = useState(false);
  const [socket, setSocket] = useState(null);
  const [alertAudio, setAlertAudio] = useState(null);
  const audioIntervalRef = useRef(null);

  const handleAuthExpired = useCallback(() => setIsAuthenticated(false), []);

  const authFetch = useCallback(
    (path, options) => adminFetch(path, options, handleAuthExpired),
    [handleAuthExpired]
  );

  // ---- Audio ----

  useEffect(() => {
    const audio = new Audio('/notification.wav');
    audio.volume = 1.0;
    setAlertAudio(audio);
    return () => {
      if (audioIntervalRef.current) clearInterval(audioIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (!alertAudio) return;
    const pendingOrders = orders.filter(o => o && o.status === 'pending');

    if (pendingOrders.length > 0) {
      if (!audioIntervalRef.current) {
        alertAudio.play().catch(() => {});
        audioIntervalRef.current = setInterval(() => {
          alertAudio.play().catch(() => {});
        }, 3000);
      }
    } else if (audioIntervalRef.current) {
      clearInterval(audioIntervalRef.current);
      audioIntervalRef.current = null;
      alertAudio.pause();
      alertAudio.currentTime = 0;
    }

    return () => {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
      }
    };
  }, [orders, alertAudio]);

  const playNotificationSound = () => {
    new Audio('/notification.wav').play().catch(() => {});
  };

  // ---- Chargement + WebSocket ----

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchProducts();
    fetchOrders();
    fetchCompletedOrders();
    fetchReservations();
    fetchCompletedReservations();
    fetchTimeSlots();
    fetchTablesConfig();
    fetchCapacityOverrides();
    fetchTableTypes();
    fetchLoyalty();
    fetchDiningTables();

    try {
      const newSocket = io(SOCKET_URL, {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });
      setSocket(newSocket);

      newSocket.on('new-order', (order) => {
        setOrders(prev => [order, ...prev]);
        playNotificationSound();
      });
      newSocket.on('order-updated', (updated) => {
        setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
      });
      newSocket.on('new-reservation', (reservation) => {
        setReservations(prev => [reservation, ...prev]);
        playNotificationSound();
      });
      newSocket.on('reservation-updated', (updated) => {
        setReservations(prev => prev.map(r => r.id === updated.id ? updated : r));
      });
      newSocket.on('products-updated', setProducts);

      return () => newSocket.close();
    } catch (error) {
      console.error('Erreur WebSocket:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ---- Fetches ----

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/products`);
      setProducts(await response.json());
    } catch (e) { console.error(e); }
  };

  const fetchOrders = async () => {
    try {
      const response = await authFetch('/orders?includeCompleted=false&limit=100');
      if (!response.ok) return;
      const data = await response.json();
      setOrders(data.orders || data);
    } catch (e) { console.error(e); }
  };

  // ---- Salle : tables physiques (QR) et équipe ----

  const fetchDiningTables = async () => {
    try {
      const response = await authFetch('/service/dining-tables');
      if (response.ok) setDiningTables(await response.json());
    } catch (e) { console.error(e); }
  };


  const addDiningTable = async () => {
    const name = newTableName.trim();
    if (!name) return;
    const response = await authFetch('/service/dining-tables', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    if (response.ok) {
      setNewTableName('');
      fetchDiningTables();
    }
  };

  const toggleDiningTable = async (table) => {
    await authFetch(`/service/dining-tables/${table.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !table.active })
    });
    fetchDiningTables();
  };

  const removeDiningTable = async (table) => {
    if (!window.confirm(`Supprimer la table "${table.name}" ? Son QR code ne fonctionnera plus.`)) return;
    await authFetch(`/service/dining-tables/${table.id}`, { method: 'DELETE' });
    fetchDiningTables();
  };

  const regenerateTableQr = async (table) => {
    if (!window.confirm(`Régénérer le QR de la table "${table.name}" ? L'ancien QR imprimé ne fonctionnera plus.`)) return;
    await authFetch(`/service/dining-tables/${table.id}/regenerate`, { method: 'POST' });
    fetchDiningTables();
  };

  // Feuille imprimable avec le QR de chaque table active
  const printQrCodes = async () => {
    const active = diningTables.filter(t => t.active);
    if (active.length === 0) return;
    const QRCode = (await import('qrcode')).default;
    const cards = await Promise.all(active.map(async (t) => {
      const url = `${window.location.origin}/table/${t.qrToken}`;
      const dataUrl = await QRCode.toDataURL(url, {
        width: 520, margin: 1, color: { dark: '#1C1C1C', light: '#FFFFFF' }
      });
      return { name: t.name, dataUrl };
    }));

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>QR codes des tables — Le Fief</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 16px; }
        .grille { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .carte { border: 2px solid #1C1C1C; border-radius: 14px; padding: 18px; text-align: center; page-break-inside: avoid; }
        .nom { font-size: 26px; font-weight: 800; margin-bottom: 4px; }
        .marque { color: #9c7440; font-weight: 700; letter-spacing: 0.06em; font-size: 13px; text-transform: uppercase; }
        img { width: 100%; max-width: 300px; }
        .aide { font-size: 14px; color: #444; margin-top: 4px; }
        @media print { .noprint { display: none; } }
      </style></head><body>
      <p class="noprint" style="text-align:center"><button onclick="window.print()" style="padding:10px 24px;font-size:16px;cursor:pointer">🖨 Imprimer</button></p>
      <div class="grille">
        ${cards.map(c => `<div class="carte">
          <div class="marque">Le Fief — American Food & Bar</div>
          <div class="nom">Table ${c.name}</div>
          <img src="${c.dataUrl}" alt="QR table ${c.name}" />
          <div class="aide">Scannez pour commander depuis votre téléphone 🍔</div>
        </div>`).join('')}
      </div>
      </body></html>`);
    w.document.close();
  };

  const fetchCompletedOrders = async () => {
    try {
      const response = await authFetch('/orders/completed?limit=20');
      if (response.ok) setCompletedOrders(await response.json());
    } catch (e) { console.error(e); }
  };

  const fetchTimeSlots = async () => {
    try {
      const response = await fetch(`${API_URL}/timeslots`);
      setTimeSlots(await response.json());
    } catch (e) { console.error(e); }
  };

  const fetchReservations = async () => {
    try {
      const response = await authFetch('/reservations?includeCompleted=false&limit=100');
      if (!response.ok) return;
      const data = await response.json();
      setReservations(data.reservations || data);
      setServicePlans({}); // les plans de table calculés ne sont plus à jour
    } catch (e) { console.error(e); }
  };

  const fetchCompletedReservations = async () => {
    try {
      const response = await authFetch('/reservations/completed?limit=20');
      if (response.ok) setCompletedReservations(await response.json());
    } catch (e) { console.error(e); }
  };

  const fetchTablesConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/tables/config`);
      setTablesConfig(await response.json());
    } catch (e) { console.error(e); }
  };

  const fetchCapacityOverrides = async () => {
    try {
      const response = await authFetch('/reservations/capacity/overrides');
      if (response.ok) setCapacityOverrides(await response.json());
    } catch (e) { console.error(e); }
  };

  // ---- Parc de tables ----

  const fetchTableTypes = async () => {
    try {
      const response = await authFetch('/tables/types');
      if (response.ok) setTableTypes(await response.json());
    } catch (e) { console.error(e); }
  };

  const addTableType = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch('/tables/types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(typeForm)
      });
      if (response.ok) {
        setTypeForm({ count: 1, seats: 2, modulable: true, label: '' });
        fetchTableTypes();
        setServicePlans({});
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.errors?.[0]?.msg || data.message || 'Erreur lors de l\'ajout');
      }
    } catch (e2) { console.error(e2); }
  };

  const saveTableType = async (type) => {
    try {
      const response = await authFetch(`/tables/types/${type.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(type)
      });
      if (response.ok) {
        fetchTableTypes();
        setServicePlans({});
      }
    } catch (e) { console.error(e); }
  };

  const deleteTableType = async (id) => {
    if (!window.confirm('Supprimer ce groupe de tables ?')) return;
    try {
      await authFetch(`/tables/types/${id}`, { method: 'DELETE' });
      fetchTableTypes();
      setServicePlans({});
    } catch (e) { console.error(e); }
  };

  // Plan de table d'un service (calcul optimisé côté serveur)
  const toggleServicePlan = async (group) => {
    const key = `${group.dateKey}|${group.service}`;
    const isOpen = openPlans[key];
    setOpenPlans(prev => ({ ...prev, [key]: !isOpen }));

    if (!isOpen && !servicePlans[key]) {
      try {
        const response = await authFetch(`/tables/plan?date=${group.dateKey}&service=${group.service}`);
        if (response.ok) {
          const plan = await response.json();
          setServicePlans(prev => ({ ...prev, [key]: plan }));
        }
      } catch (e) { console.error(e); }
    }
  };

  // ---- Fermetures exceptionnelles / réglages / notifications ----

  const fetchOps = async () => {
    try {
      const [cRes, sRes, pRes] = await Promise.all([
        authFetch('/closures'),
        authFetch('/settings/app'),
        authFetch('/push/status')
      ]);
      if (cRes.ok) setClosures(await cRes.json());
      if (sRes.ok) setAppSettings(await sRes.json());
      if (pRes.ok) setPushStatus(await pRes.json());
    } catch (e) { console.error(e); }

    // Cet appareil est-il déjà abonné aux notifications ?
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushThisDevice(Boolean(sub));
      }
    } catch { /* pas bloquant */ }
  };

  const addClosure = async () => {
    if (!closureForm.startDate || !closureForm.endDate) {
      alert('Dates de début et de fin requises');
      return;
    }
    try {
      const res = await authFetch('/closures', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(closureForm)
      });
      if (res.ok) {
        setClosureForm({ startDate: null, endDate: null, scope: 'all', note: '' });
        fetchOps();
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.errors?.[0]?.msg || d.message || 'Erreur');
      }
    } catch (e) { console.error(e); }
  };

  const removeClosure = async (id) => {
    if (!window.confirm('Supprimer cette fermeture ?')) return;
    try {
      await authFetch(`/closures/${id}`, { method: 'DELETE' });
      fetchOps();
    } catch (e) { console.error(e); }
  };

  const saveAppSettings = async () => {
    try {
      const res = await authFetch('/settings/app', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appSettings)
      });
      if (res.ok) { setAppSettings(await res.json()); alert('Réglages enregistrés'); }
      else { const d = await res.json().catch(() => ({})); alert(d.message || 'Erreur'); }
    } catch (e) { console.error(e); }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  };

  const enablePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      alert('Notifications non supportées par ce navigateur. Sur iPhone : ajoutez d\'abord le site à l\'écran d\'accueil.');
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        alert('Autorisation refusée — activez les notifications dans les réglages du navigateur.');
        return;
      }
      const st = pushStatus || {};
      if (!st.enabled || !st.publicKey) {
        alert('Notifications non configurées côté serveur.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(st.publicKey)
      });
      const res = await authFetch('/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub.toJSON())
      });
      if (res.ok) {
        setPushThisDevice(true);
        fetchOps();
        authFetch('/push/test', { method: 'POST' });
      }
    } catch (e) {
      console.error(e);
      alert('Impossible d\'activer les notifications sur cet appareil.');
    }
  };

  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await authFetch('/push/unsubscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint })
        });
        await sub.unsubscribe();
      }
      setPushThisDevice(false);
      fetchOps();
    } catch (e) { console.error(e); }
  };

  // ---- Ticket cuisine imprimable ----

  const printTicket = (order) => {
    const items = order.items.map(i =>
      `<tr><td class="q">${i.quantity}×</td><td>${i.name}${i.paidWithPoints > 0 ? ' <em>(offert fidélité)</em>' : ''}${i.notes ? `<div class="note">▶ ${i.notes}</div>` : ''}</td></tr>`
    ).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Commande #${order.id}</title>
<style>
  body { font-family: 'Courier New', monospace; width: 260px; margin: 0; padding: 10px; font-size: 13px; color: #000; }
  h1 { font-size: 16px; text-align: center; margin: 0 0 4px; }
  .heure { font-size: 22px; font-weight: bold; text-align: center; margin: 6px 0; }
  .sep { border-top: 1px dashed #000; margin: 8px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 3px 0; vertical-align: top; }
  td.q { width: 34px; font-weight: bold; }
  .note { font-size: 12px; font-style: italic; padding-left: 4px; }
  .total { text-align: right; font-weight: bold; font-size: 14px; margin-top: 6px; }
  .client { text-align: center; margin: 4px 0; }
  em { font-size: 11px; }
</style></head><body>
  <h1>LE FIEF — Commande #${order.id}</h1>
  <div class="heure">Retrait ${formatParisTime(order.pickupTime)}</div>
  <div class="client">${order.customerName} — ${order.customerPhone}</div>
  <div class="sep"></div>
  <table>${items}</table>
  <div class="sep"></div>
  <div class="total">À encaisser : ${parseFloat(order.total).toFixed(2)}€</div>
  <script>window.onload = () => { window.print(); }</scr${''}ipt>
</body></html>`;
    const win = window.open('', '_blank', 'width=340,height=600');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  // ---- Gestion d'un client (admin) ----

  const openCustomer = (c) => {
    if (managedCustomerId === c.id) { setManagedCustomerId(null); return; }
    setManagedCustomerId(c.id);
    setCustEdit({ name: c.name, phone: formatPhoneFR(c.phone), email: c.email || '', marketingOptin: c.marketingOptin });
    setPointsAdjust({ points: '', label: '' });
    setCustMsg('');
  };

  const flashCust = (m) => { setCustMsg(m); setTimeout(() => setCustMsg(''), 2500); };

  const saveCustomer = async () => {
    try {
      const res = await authFetch(`/customers/admin/${managedCustomerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(custEdit)
      });
      if (res.ok) { fetchLoyalty(); flashCust('Infos client mises à jour'); }
      else { const d = await res.json().catch(() => ({})); alert(d.errors?.[0]?.msg || d.message || 'Erreur'); }
    } catch (e) { console.error(e); }
  };

  const adjustCustomerPoints = async (sign) => {
    const amount = (parseInt(pointsAdjust.points) || 0) * sign;
    if (!amount) { alert('Indiquez un nombre de points'); return; }
    try {
      const res = await authFetch(`/customers/admin/${managedCustomerId}/points`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ points: amount, label: pointsAdjust.label })
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setPointsAdjust({ points: '', label: '' }); fetchLoyalty(); flashCust(`Nouveau solde : ${d.balance} points`); }
      else { alert(d.message || 'Erreur'); }
    } catch (e) { console.error(e); }
  };

  const resetCustomerPassword = async () => {
    const newPassword = window.prompt('Nouveau mot de passe pour ce client (6 caractères min.) :');
    if (!newPassword) return;
    if (newPassword.length < 6) { alert('6 caractères minimum'); return; }
    try {
      const res = await authFetch(`/customers/admin/${managedCustomerId}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword })
      });
      if (res.ok) flashCust('Mot de passe réinitialisé');
      else alert('Erreur');
    } catch (e) { console.error(e); }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${c.name} ? (ses points seront perdus)`)) return;
    try {
      const res = await authFetch(`/customers/admin/${c.id}`, { method: 'DELETE' });
      if (res.ok) { setManagedCustomerId(null); fetchLoyalty(); }
    } catch (e) { console.error(e); }
  };

  const fetchStats = async (period = statsPeriod) => {
    try {
      const response = await authFetch(`/stats?period=${period}`);
      if (response.ok) setStats(await response.json());
    } catch (e) { console.error(e); }
  };

  const fetchLoyalty = async () => {
    try {
      const [settingsRes, customersRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/customers/loyalty/settings`),
        authFetch('/customers/admin/list'),
        authFetch('/customers/admin/loyalty/events')
      ]);
      if (settingsRes.ok) setLoyaltySettings(await settingsRes.json());
      if (customersRes.ok) setLoyaltyCustomers(await customersRes.json());
      if (eventsRes.ok) setLoyaltyEvents(await eventsRes.json());
    } catch (e) { console.error(e); }
  };

  // ---- Actions ----

  const saveLoyaltySettings = async () => {
    try {
      const response = await authFetch('/customers/admin/loyalty/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loyaltySettings)
      });
      if (response.ok) {
        setLoyaltySettings(await response.json());
        setLoyaltySaved(true);
        setTimeout(() => setLoyaltySaved(false), 2500);
      } else {
        alert('Erreur lors de l\'enregistrement');
      }
    } catch (e) { console.error(e); }
  };

  const createLoyaltyEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.name || !eventForm.startDate || !eventForm.endDate) {
      alert('Nom, date de début et date de fin sont requis');
      return;
    }
    try {
      const response = await authFetch('/customers/admin/loyalty/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventForm)
      });
      if (response.ok) {
        setEventForm({ name: '', multiplier: 2, startDate: null, endDate: null, service: 'all' });
        fetchLoyalty();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.errors?.[0]?.msg || data.message || 'Erreur lors de la création');
      }
    } catch (e2) { console.error(e2); }
  };

  const toggleLoyaltyEvent = async (id) => {
    try {
      await authFetch(`/customers/admin/loyalty/events/${id}/toggle`, { method: 'PATCH' });
      fetchLoyalty();
    } catch (e) { console.error(e); }
  };

  const deleteLoyaltyEvent = async (id) => {
    if (!window.confirm('Supprimer cette soirée fidélité ?')) return;
    try {
      await authFetch(`/customers/admin/loyalty/events/${id}`, { method: 'DELETE' });
      fetchLoyalty();
    } catch (e) { console.error(e); }
  };

  const addCapacityOverride = async () => {
    if (!newOverride.date || !newOverride.capacity) {
      alert('Date et capacité requises');
      return;
    }
    try {
      await authFetch('/reservations/capacity/override', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newOverride)
      });
      setNewOverride({ date: null, service: 'both', capacity: 30, note: '' });
      fetchCapacityOverrides();
    } catch (e) { console.error(e); }
  };

  const deleteCapacityOverride = async (id) => {
    if (!window.confirm('Supprimer cette capacité exceptionnelle ?')) return;
    try {
      await authFetch(`/reservations/capacity/override/${id}`, { method: 'DELETE' });
      fetchCapacityOverrides();
    } catch (e) { console.error(e); }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch('/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm)
      });
      if (response.ok) {
        fetchProducts();
        setProductForm({ name: '', price: '', category: '', description: '', points_price: '' });
        setShowAddProduct(false);
      }
    } catch (e2) { console.error(e2); }
  };

  // Ouvre/ferme l'édition complète d'un produit
  const startEditProduct = (product) => {
    setEditingProductId(product.id);
    setEditProductForm({
      name: product.name,
      price: product.price,
      category: product.category,
      description: product.description || '',
      points_price: product.points_price || '',
      options: Array.isArray(product.options) ? product.options.map(o => ({ ...o })) : []
    });
  };

  const saveEditProduct = async () => {
    try {
      const response = await authFetch(`/products/${editingProductId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editProductForm,
          points_price: editProductForm.points_price === '' ? null : parseInt(editProductForm.points_price),
          options: editProductForm.options
            .filter(o => String(o.name || '').trim())
            .map(o => ({ name: String(o.name).trim(), price: parseFloat(o.price) || 0 }))
        })
      });
      if (response.ok) {
        setEditingProductId(null);
        setEditProductForm(null);
        fetchProducts();
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.errors?.[0]?.msg || data.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (e) { console.error(e); }
  };

  const toggleProductAvailability = async (id) => {
    try {
      await authFetch(`/products/${id}/toggle`, { method: 'PATCH' });
      fetchProducts();
    } catch (e) { console.error(e); }
  };

  const toggleProductCanTakeAway = async (id) => {
    try {
      await authFetch(`/products/${id}/toggle-take-away`, { method: 'PATCH' });
      fetchProducts();
    } catch (e) { console.error(e); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await authFetch(`/products/${id}`, { method: 'DELETE' });
      fetchProducts();
    } catch (e) { console.error(e); }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await authFetch(`/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchOrders();
      if (newStatus === 'completed' || newStatus === 'cancelled') {
        fetchCompletedOrders();
        fetchLoyalty();
      }
    } catch (e) { console.error(e); }
  };

  // --- Édition d'une commande à emporter ---

  const startEditOrder = (order) => {
    setOrderEditId(order.id);
    setOrderEditItems(order.items.map(it => ({
      id: it.product_id, name: it.name, price: parseFloat(it.price),
      quantity: it.quantity, notes: it.notes || '', payWithPoints: it.paidWithPoints > 0
    })));
    setOrderEditTime(order.pickupTime ? new Date(order.pickupTime).toISOString().slice(0, 16) : '');
    setOrderEditPickerCat('');
  };

  const cancelEditOrder = () => {
    setOrderEditId(null);
    setOrderEditItems([]);
    setOrderEditTime('');
  };

  const editOrderAddProduct = (p) => {
    setOrderEditItems(prev => {
      const ex = prev.find(i => i.id === p.id && !i.notes && !i.payWithPoints);
      if (ex) return prev.map(i => i === ex ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { id: p.id, name: p.name, price: parseFloat(p.price), quantity: 1, notes: '', payWithPoints: false }];
    });
  };

  const editOrderSetQty = (idx, q) => {
    setOrderEditItems(prev => q <= 0 ? prev.filter((_, i) => i !== idx) : prev.map((it, i) => i === idx ? { ...it, quantity: q } : it));
  };

  const saveOrderEdit = async () => {
    if (orderEditItems.length === 0) {
      window.alert('Une commande doit contenir au moins un article. Pour tout retirer, annulez la commande.');
      return;
    }
    try {
      const payload = {
        items: orderEditItems.map(it => ({
          id: it.id, quantity: it.quantity,
          notes: it.notes || undefined,
          payWithPoints: it.payWithPoints || undefined
        }))
      };
      if (orderEditTime) payload.pickupTime = new Date(orderEditTime).toISOString();
      const response = await authFetch(`/orders/${orderEditId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        cancelEditOrder();
        fetchOrders();
      } else {
        const body = await response.json().catch(() => ({}));
        window.alert(body.message || (body.errors && body.errors[0] && body.errors[0].msg) || 'Erreur');
      }
    } catch (e) { console.error(e); }
  };

  // --- Historique paginé (commandes ou réservations) ---

  const fetchHistory = useCallback(async (type, filters, page) => {
    setHistLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (filters.q) params.set('q', filters.q);
      if (filters.status !== 'all') params.set('status', filters.status);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.channelOrService !== 'all') {
        params.set(type === 'orders' ? 'channel' : 'service', filters.channelOrService);
      }
      const response = await authFetch(`/${type === 'orders' ? 'orders' : 'reservations'}/history?${params}`);
      if (response.ok) {
        const body = await response.json();
        setHistData({
          rows: type === 'orders' ? body.orders : body.reservations,
          total: body.pagination.total,
          page: body.pagination.page,
          totalPages: body.pagination.totalPages
        });
      }
    } catch (e) { console.error(e); }
    setHistLoading(false);
  }, [authFetch]);

  // Recharge l'historique quand l'onglet est actif (anti-rebond sur la recherche)
  useEffect(() => {
    if (activeTab !== 'history') return;
    const t = setTimeout(() => fetchHistory(histType, histFilters, histPage), 250);
    return () => clearTimeout(t);
  }, [activeTab, histType, histFilters, histPage, fetchHistory]);

  const updateTimeSlot = async (day, field, value) => {
    const updatedSlots = {
      ...timeSlots,
      [day]: { ...timeSlots[day], [field]: value }
    };
    try {
      await authFetch('/timeslots', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSlots)
      });
      setTimeSlots(updatedSlots);
    } catch (e) { console.error(e); }
  };

  const updateReservationStatus = async (id, newStatus) => {
    try {
      await authFetch(`/reservations/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchReservations();
      if (newStatus === 'completed' || newStatus === 'cancelled') fetchCompletedReservations();
    } catch (e) { console.error(e); }
  };

  const completeServiceReservations = async (group) => {
    const label = `${formatDayLabel(group.dateKey)} ${SERVICE_META[group.service].label}`;
    if (!window.confirm(`Marquer les ${group.reservations.length} réservation(s) du service « ${label} » comme honorées ?`)) return;
    try {
      await Promise.all(group.reservations.map(r =>
        authFetch(`/reservations/${r.id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'completed' })
        })
      ));
      fetchReservations();
      fetchCompletedReservations();
    } catch (e) { console.error(e); }
  };

  const deleteReservation = async (id) => {
    if (!window.confirm('Supprimer cette réservation ?')) return;
    try {
      await authFetch(`/reservations/${id}`, { method: 'DELETE' });
      fetchReservations();
    } catch (e) { console.error(e); }
  };

  // --- Édition / ajout de réservation ---

  const emptyResaForm = () => ({
    date: dateToKey(new Date()),
    service: new Date().getHours() < 16 ? 'lunch' : 'dinner', // service le plus probable
    time: '', guests: 2, customerName: '', customerPhone: '', notes: ''
  });

  const startEditResa = (r) => {
    setShowAddResa(false);
    setResaWarning('');
    setResaEditId(r.id);
    setResaForm({
      date: String(r.date).slice(0, 10),
      service: r.service,
      time: r.time ? r.time.slice(0, 5) : '',
      guests: r.guests,
      customerName: r.customerName,
      customerPhone: r.customerPhone,
      notes: r.notes || ''
    });
  };

  const saveResaEdit = async () => {
    setResaWarning('');
    try {
      const response = await authFetch(`/reservations/${resaEditId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...resaForm, guests: parseInt(resaForm.guests) })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setResaWarning(body.message || (body.errors && body.errors[0] && body.errors[0].msg) || 'Erreur');
        return;
      }
      setResaEditId(null);
      setResaForm(null);
      fetchReservations();
      // Rafraîchir le plan du service concerné s'il est ouvert
      setServicePlans({});
      if (body.warning) window.alert(body.warning);
    } catch (e) { console.error(e); }
  };

  const createAdminResa = async () => {
    setResaWarning('');
    try {
      const response = await authFetch('/reservations/admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...resaForm, guests: parseInt(resaForm.guests) })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setResaWarning(body.message || (body.errors && body.errors[0] && body.errors[0].msg) || 'Erreur');
        return;
      }
      setShowAddResa(false);
      setResaForm(null);
      fetchReservations();
      setServicePlans({});
    } catch (e) { console.error(e); }
  };

  const getStatusBadge = (status) => {
    const badge = STATUS_BADGES[status] || STATUS_BADGES.pending;
    return <span className={`adm-badge ${badge.class}`}>{badge.text}</span>;
  };

  const handleLogout = () => {
    if (socket) socket.close();
    if (audioIntervalRef.current) {
      clearInterval(audioIntervalRef.current);
      audioIntervalRef.current = null;
    }
    if (alertAudio) {
      alertAudio.pause();
      alertAudio.currentTime = 0;
    }
    clearAdminToken();
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  // ---- Données dérivées ----

  // Les commandes sur place (tables QR / serveur) vivent dans les écrans
  // Cuisine et Caisse : l'onglet Commandes reste dédié au click & collect.
  const activeOrders = orders.filter(o => o && ['pending', 'preparing', 'ready'].includes(o.status));
  const pendingOrders = activeOrders.filter(o => o.channel !== 'dine_in');
  const dineInCount = activeOrders.length - pendingOrders.length;
  const activeReservations = reservations.filter(r => r && r.status === 'confirmed');
  const truePendingOrders = orders.filter(o => o && o.status === 'pending');
  const orderDayGroups = groupOrdersByDay(pendingOrders);
  const { upcoming: upcomingServices, past: pastServices } = groupReservationsByService(activeReservations);
  const pastCount = pastServices.reduce((s, g) => s + g.reservations.length, 0);

  const TABS = [
    { key: 'orders', label: 'Commandes', icon: '🧾', count: pendingOrders.length },
    { key: 'reservations', label: 'Réservations', icon: '📅', count: activeReservations.length },
    { key: 'products', label: 'Carte', icon: '🍔' },
    { key: 'loyalty', label: 'Fidélité', icon: '⭐' },
    { key: 'stats', label: 'Statistiques', icon: '📊' },
    { key: 'history', label: 'Historique', icon: '🗂️' },
    { key: 'settings', label: 'Paramètres', icon: '⚙️' }
  ];

  // ---- Rendus ----

  // Formulaire RAPIDE de saisie (pensé mobile / pendant l'appel du client) :
  // aucun champ obligatoire, gros boutons, détails repliés.
  const QUICK_GUESTS = [1, 2, 3, 4, 5, 6];
  const renderQuickResaForm = () => (
    <div className="qresa">
      <div className="qresa-bloc">
        <span className="qresa-label">Couverts</span>
        <div className="qresa-guests">
          {QUICK_GUESTS.map(n => (
            <button key={n} type="button"
              className={`qresa-guest ${parseInt(resaForm.guests) === n ? 'actif' : ''}`}
              onClick={() => setResaForm({ ...resaForm, guests: n })}>{n}</button>
          ))}
          <div className="qresa-stepper">
            <button type="button" onClick={() => setResaForm({ ...resaForm, guests: Math.max(1, (parseInt(resaForm.guests) || 1) - 1) })}>−</button>
            <span>{resaForm.guests}</span>
            <button type="button" onClick={() => setResaForm({ ...resaForm, guests: Math.min(50, (parseInt(resaForm.guests) || 1) + 1) })}>+</button>
          </div>
        </div>
      </div>

      <div className="qresa-bloc">
        <span className="qresa-label">Quand</span>
        <div className="qresa-chips">
          <button type="button" className={`qresa-chip ${resaForm.date === dateToKey(new Date()) ? 'actif' : ''}`}
            onClick={() => setResaForm({ ...resaForm, date: dateToKey(new Date()) })}>Aujourd'hui</button>
          <button type="button" className={`qresa-chip ${resaForm.date === dateToKey(new Date(Date.now() + 864e5)) ? 'actif' : ''}`}
            onClick={() => setResaForm({ ...resaForm, date: dateToKey(new Date(Date.now() + 864e5)) })}>Demain</button>
          <input type="date" className="qresa-date" value={resaForm.date}
            onChange={(e) => setResaForm({ ...resaForm, date: e.target.value })} />
        </div>
        <div className="qresa-chips">
          <button type="button" className={`qresa-chip ${resaForm.service === 'lunch' ? 'actif' : ''}`}
            onClick={() => setResaForm({ ...resaForm, service: 'lunch' })}>☀️ Midi</button>
          <button type="button" className={`qresa-chip ${resaForm.service === 'dinner' ? 'actif' : ''}`}
            onClick={() => setResaForm({ ...resaForm, service: 'dinner' })}>🌙 Soir</button>
        </div>
      </div>

      <div className="qresa-bloc">
        <span className="qresa-label">Nom <span className="champ-option">(optionnel)</span></span>
        <input type="text" className="qresa-nom" placeholder="Ex : Dupont"
          value={resaForm.customerName}
          onChange={(e) => setResaForm({ ...resaForm, customerName: e.target.value })} />
      </div>

      <button type="button" className="qresa-plus" onClick={() => setShowResaDetails(!showResaDetails)}>
        {showResaDetails ? '− Moins de détails' : '+ Heure, téléphone, remarques'}
      </button>
      {showResaDetails && (
        <div className="qresa-details">
          <div className="adm-champ">
            <label>Heure</label>
            <input type="time" value={resaForm.time}
              onChange={(e) => setResaForm({ ...resaForm, time: e.target.value })} />
          </div>
          <div className="adm-champ">
            <label>Téléphone</label>
            <input type="tel" value={resaForm.customerPhone}
              onChange={(e) => setResaForm({ ...resaForm, customerPhone: e.target.value })} />
          </div>
          <div className="adm-champ qresa-details-notes">
            <label>Remarques</label>
            <input type="text" value={resaForm.notes}
              onChange={(e) => setResaForm({ ...resaForm, notes: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );

  // Champs partagés pour l'édition d'une réservation
  const renderResaFields = () => (
    <div className="resa-form-grille">
      <div className="adm-champ">
        <label>Nom</label>
        <input type="text" value={resaForm.customerName}
          onChange={(e) => setResaForm({ ...resaForm, customerName: e.target.value })} />
      </div>
      <div className="adm-champ">
        <label>Téléphone</label>
        <input type="tel" value={resaForm.customerPhone}
          onChange={(e) => setResaForm({ ...resaForm, customerPhone: e.target.value })} />
      </div>
      <div className="adm-champ">
        <label>Date</label>
        <input type="date" value={resaForm.date}
          onChange={(e) => setResaForm({ ...resaForm, date: e.target.value })} />
      </div>
      <div className="adm-champ">
        <label>Service</label>
        <select value={resaForm.service} onChange={(e) => setResaForm({ ...resaForm, service: e.target.value })}>
          <option value="lunch">Midi</option>
          <option value="dinner">Soir</option>
        </select>
      </div>
      <div className="adm-champ">
        <label>Heure <span className="champ-option">(optionnel)</span></label>
        <input type="time" value={resaForm.time}
          onChange={(e) => setResaForm({ ...resaForm, time: e.target.value })} />
      </div>
      <div className="adm-champ">
        <label>Couverts</label>
        <input type="number" min="1" max="50" value={resaForm.guests}
          onChange={(e) => setResaForm({ ...resaForm, guests: e.target.value })} />
      </div>
      <div className="adm-champ resa-form-notes">
        <label>Remarques <span className="champ-option">(allergies, poussette…)</span></label>
        <input type="text" value={resaForm.notes}
          onChange={(e) => setResaForm({ ...resaForm, notes: e.target.value })} />
      </div>
    </div>
  );

  const renderReservationCard = (reservation) => {
    if (resaEditId === reservation.id && resaForm) {
      return (
        <div key={reservation.id} className="adm-carte resa-carte resa-carte-edit">
          <strong className="resa-edit-titre">Modifier la réservation #{reservation.id}</strong>
          {renderResaFields()}
          {resaWarning && <p className="adm-aide" style={{ color: 'var(--rouge, #b3362c)' }}>{resaWarning}</p>}
          <div className="adm-actions">
            <button className="adm-btn adm-btn-principal" onClick={saveResaEdit}>Enregistrer</button>
            <button className="adm-btn adm-btn-discret" onClick={() => { setResaEditId(null); setResaForm(null); }}>Annuler</button>
          </div>
          <p className="adm-aide">La modification est répercutée automatiquement sur le plan de salle du service.</p>
        </div>
      );
    }
    return (
      <div key={reservation.id} className="adm-carte resa-carte">
        <div className="resa-carte-haut">
          <div className="resa-carte-qui">
            {reservation.time && <span className="resa-heure">{reservation.time.slice(0, 5)}</span>}
            <div>
              <strong className="resa-nom">{reservation.customerName}</strong>
              {reservation.customerPhone
                ? <p className="resa-tel"><a href={`tel:${reservation.customerPhone}`}>{reservation.customerPhone}</a></p>
                : <p className="resa-tel resa-tel-vide">Sans téléphone</p>}
            </div>
          </div>
          <span className="resa-couverts">{reservation.guests}<small> pers.</small></span>
        </div>

        {reservation.notes && <p className="resa-notes">💬 {reservation.notes}</p>}

        <div className="adm-actions">
          <button className="adm-btn adm-btn-ok" onClick={() => updateReservationStatus(reservation.id, 'completed')}>
            ✓ Honorée
          </button>
          <button className="adm-btn adm-btn-principal" onClick={() => startEditResa(reservation)}>
            ✎ Modifier
          </button>
          <button className="adm-btn adm-btn-mauvais" onClick={() => updateReservationStatus(reservation.id, 'cancelled')}>
            Annuler
          </button>
          <button className="adm-btn adm-btn-discret" onClick={() => deleteReservation(reservation.id)}>
            Supprimer
          </button>
        </div>
      </div>
    );
  };

  const renderServiceGroup = (group) => {
    const meta = SERVICE_META[group.service];
    const current = isServiceNow(group.dateKey, group.service);
    const planKey = `${group.dateKey}|${group.service}`;
    const plan = servicePlans[planKey];
    const planOpen = openPlans[planKey];
    const hasTablePark = tableTypes.types.some(t => t.count > 0);

    return (
      <section key={planKey} className={`svc-groupe ${current ? 'svc-groupe-actif' : ''}`}>
        <header className="svc-groupe-tete">
          <div className="svc-groupe-titre">
            <span>{meta.emoji}</span>
            <strong>{formatDayLabel(group.dateKey)} — {meta.label}</strong>
            {current && <span className="svc-en-cours">En ce moment</span>}
          </div>
          <div className="svc-groupe-outils">
            <span className="svc-stats">
              {group.reservations.length} table{group.reservations.length > 1 ? 's' : ''} · {group.guests} couvert{group.guests > 1 ? 's' : ''}
            </span>
            {hasTablePark && (
              <button className="adm-btn adm-btn-discret" onClick={() => toggleServicePlan(group)}>
                🪑 {planOpen ? 'Masquer le plan' : 'Plan de table'}
              </button>
            )}
          </div>
        </header>

        {planOpen && (
          <div className="svc-plan">
            {!plan ? (
              <p className="svc-plan-chargement">Calcul de la disposition optimale…</p>
            ) : plan.mode === 'legacy' ? (
              <p className="svc-plan-chargement">Déclarez vos tables pour obtenir un plan.</p>
            ) : (
              <>
                {!plan.feasible && (
                  <div className="svc-plan-alerte">
                    ⚠️ Impossible d'asseoir tout le monde avec les tables actuelles —
                    {' '}{plan.unseated.length} réservation{plan.unseated.length > 1 ? 's' : ''} sans table :
                    {' '}{plan.unseated.map(u => `${u.name} (${u.guests})`).join(', ')}
                  </div>
                )}
                <div className="svc-plan-lignes">
                  {plan.assignments.map(a => (
                    <div key={a.reservationId} className="svc-plan-ligne">
                      <span className="svc-plan-heure">{a.time || '—'}</span>
                      <span className="svc-plan-qui">
                        <strong>{a.name}</strong> · {a.guests} pers.
                      </span>
                      <span className={`svc-plan-tables ${a.combined ? 'combine' : ''}`}>
                        {a.tables}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="svc-plan-pied">
                  <span>
                    {plan.remainingTables.length === 0
                      ? 'Toutes les tables sont utilisées.'
                      : `Tables libres : ${plan.remainingTables.map(t =>
                          `${t.count} × ${t.seats} pl.${t.label ? ` (${t.label})` : ''}`
                        ).join(' · ')}`}
                  </span>
                  <span className="svc-plan-waste">
                    {plan.totalWaste === 0
                      ? 'Disposition parfaite, aucune place perdue'
                      : `${plan.totalWaste} place${plan.totalWaste > 1 ? 's' : ''} perdue${plan.totalWaste > 1 ? 's' : ''}`}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        <div className="svc-groupe-corps">
          {group.reservations.map(renderReservationCard)}
        </div>
      </section>
    );
  };

  return (
    <div className="adm">
      {/* ===== Barre du haut ===== */}
      <header className="adm-tete">
        <div className="adm-tete-gauche">
          <img src="/logo.svg" alt="Le Fief" className="adm-logo" />
          <span className="adm-tete-titre">Administration</span>
        </div>
        <div className="adm-tete-droite">
          {truePendingOrders.length > 0 && (
            <span className="adm-alerte">
              <span className="adm-alerte-dot" />
              {truePendingOrders.length} nouvelle{truePendingOrders.length > 1 ? 's' : ''} commande{truePendingOrders.length > 1 ? 's' : ''}
            </span>
          )}
          <button className="adm-btn adm-btn-discret" onClick={handleLogout}>Déconnexion</button>
        </div>
      </header>

      {/* ===== Onglets ===== */}
      <nav className="adm-onglets">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`adm-onglet ${activeTab === tab.key ? 'actif' : ''}`}
            onClick={() => {
              setActiveTab(tab.key);
              if (tab.key === 'loyalty') fetchLoyalty();
              if (tab.key === 'stats') fetchStats();
              if (tab.key === 'settings') fetchOps();
            }}
          >
            <span className="adm-onglet-icone">{tab.icon}</span>
            {tab.label}
            {tab.count > 0 && <span className="adm-onglet-count">{tab.count}</span>}
          </button>
        ))}
      </nav>

      <main className="adm-contenu">

        {/* ===================== COMMANDES ===================== */}
        {activeTab === 'orders' && (
          <>
            {dineInCount > 0 && (
              <div className="adm-carte adm-carte-pad" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span>🍽️ <strong>{dineInCount} envoi{dineInCount > 1 ? 's' : ''} sur place</strong> en cours sur les tables</span>
                <a className="adm-btn adm-btn-discret" href="/cuisine" target="_blank" rel="noreferrer">Écran cuisine</a>
                <a className="adm-btn adm-btn-discret" href="/caisse" target="_blank" rel="noreferrer">Caisse</a>
              </div>
            )}
            <h2 className="adm-section-titre">Commandes en cours (à emporter)</h2>
            {pendingOrders.length === 0 ? (
              <p className="adm-vide">Aucune commande en cours — tout est à jour 👌</p>
            ) : (
              orderDayGroups.map(dayGroup => (
                <section key={dayGroup.dateKey} className="jour-groupe">
                  <header className="jour-groupe-tete">
                    <strong>{formatDayLabel(dayGroup.dateKey)}</strong>
                    <span>{dayGroup.orders.length} commande{dayGroup.orders.length > 1 ? 's' : ''}</span>
                  </header>
                  <div className="commandes-grille">
                    {dayGroup.orders.map(order => {
                      const mins = minutesUntil(order.pickupTime);
                      const isSoon = mins <= 45 && mins > -60;
                      return (
                        <article key={order.id} className={`adm-carte cmd-carte ${order.status === 'pending' ? 'cmd-nouvelle' : ''}`}>
                          <div className="cmd-tete">
                            <div className="cmd-heure">
                              <strong>{formatParisTime(order.pickupTime)}</strong>
                              {isSoon && (
                                <span className="cmd-imminente">{mins <= 0 ? 'Maintenant' : `dans ${mins} min`}</span>
                              )}
                            </div>
                            {getStatusBadge(order.status)}
                          </div>

                          <p className="cmd-ref">
                            #{order.id} · {formatParisDateTime(order.pickupTime)}
                          </p>

                          <div className="cmd-client">
                            <strong>{order.customerName}</strong>
                            {order.customerId && <span className="cmd-fidele">⭐ fidélité</span>}
                            <a href={`tel:${order.customerPhone}`}>{order.customerPhone}</a>
                          </div>

                          <div className="cmd-articles">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="cmd-article">
                                <div className="cmd-article-ligne">
                                  <span>{item.name} <em>×{item.quantity}</em></span>
                                  <span>{(parseFloat(item.price) * item.quantity).toFixed(2)}€</span>
                                </div>
                                {item.notes && <p className="cmd-article-note">💬 {item.notes}</p>}
                              </div>
                            ))}
                            {parseFloat(order.discount || 0) > 0 && (
                              <div className="cmd-article-ligne cmd-reduction">
                                <span>⭐ Article(s) offert(s)</span>
                                <span>−{parseFloat(order.discount).toFixed(2)}€</span>
                              </div>
                            )}
                            <div className="cmd-total">
                              <span>Total</span>
                              <strong>{parseFloat(order.total).toFixed(2)}€</strong>
                            </div>
                          </div>

                          <div className="adm-actions">
                            {order.status === 'pending' && (
                              <button className="adm-btn adm-btn-principal" onClick={() => updateOrderStatus(order.id, 'preparing')}>
                                Commencer la préparation
                              </button>
                            )}
                            {order.status === 'preparing' && (
                              <button className="adm-btn adm-btn-principal" onClick={() => updateOrderStatus(order.id, 'ready')}>
                                Marquer comme prêt
                              </button>
                            )}
                            {order.status === 'ready' && (
                              <button className="adm-btn adm-btn-ok" onClick={() => updateOrderStatus(order.id, 'completed')}>
                                ✓ Récupérée
                              </button>
                            )}
                            <button className="adm-btn adm-btn-discret" onClick={() => orderEditId === order.id ? cancelEditOrder() : startEditOrder(order)}>
                              ✎ Modifier
                            </button>
                            <button className="adm-btn adm-btn-mauvais" onClick={() => updateOrderStatus(order.id, 'cancelled')}>
                              Annuler
                            </button>
                            <button className="adm-btn adm-btn-discret" onClick={() => printTicket(order)} title="Imprimer le ticket cuisine">
                              🖨 Ticket
                            </button>
                          </div>

                          {orderEditId === order.id && (
                            <div className="cmd-edit">
                              <strong className="cmd-edit-titre">Modifier la commande</strong>
                              <div className="cmd-edit-lignes">
                                {orderEditItems.map((it, idx) => (
                                  <div key={idx} className="cmd-edit-ligne">
                                    <span className="cmd-edit-nom">{it.name}{it.payWithPoints ? ' ⭐' : ''}</span>
                                    <div className="cmd-edit-qty">
                                      <button onClick={() => editOrderSetQty(idx, it.quantity - 1)}>−</button>
                                      <span>{it.quantity}</span>
                                      <button onClick={() => editOrderSetQty(idx, it.quantity + 1)}>+</button>
                                    </div>
                                  </div>
                                ))}
                                {orderEditItems.length === 0 && <p className="adm-aide">Aucun article — ajoutez-en ci-dessous.</p>}
                              </div>

                              <div className="cmd-edit-ajout">
                                <select value={orderEditPickerCat} onChange={(e) => setOrderEditPickerCat(e.target.value)}>
                                  <option value="">+ Ajouter un article…</option>
                                  {[...new Set(products.filter(p => p.available && p.can_take_away).map(p => p.category))].map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                                {orderEditPickerCat && (
                                  <div className="cmd-edit-produits">
                                    {products.filter(p => p.available && p.can_take_away && p.category === orderEditPickerCat).map(p => (
                                      <button key={p.id} className="adm-btn adm-btn-discret" onClick={() => editOrderAddProduct(p)}>
                                        {p.name} · {parseFloat(p.price).toFixed(2)}€
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="adm-champ">
                                <label>Heure de retrait</label>
                                <input type="datetime-local" value={orderEditTime}
                                  onChange={(e) => setOrderEditTime(e.target.value)} />
                              </div>

                              <div className="adm-actions">
                                <button className="adm-btn adm-btn-principal" onClick={saveOrderEdit}>Enregistrer les modifications</button>
                                <button className="adm-btn adm-btn-discret" onClick={cancelEditOrder}>Annuler</button>
                              </div>
                              <p className="adm-aide">Les prix sont recalculés depuis la carte. Les points de fidélité sont réajustés automatiquement.</p>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))
            )}

            <h2 className="adm-section-titre">Historique récent</h2>
            {completedOrders.length === 0 ? (
              <p className="adm-vide">Aucune commande terminée</p>
            ) : (
              <div className="adm-carte adm-liste">
                {completedOrders.slice(0, 10).map(order => (
                  <div key={order.id} className="adm-liste-ligne">
                    <span>#{order.id} — {order.customerName}</span>
                    <span className="adm-liste-fin">
                      <span>{parseFloat(order.total).toFixed(2)}€</span>
                      {getStatusBadge(order.status)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===================== RÉSERVATIONS ===================== */}
        {activeTab === 'reservations' && (
          <>
            <div className="adm-section-tete">
              <h2 className="adm-section-titre" style={{ margin: 0 }}>Services à venir</h2>
              <button className="adm-btn adm-btn-principal" onClick={() => {
                setResaEditId(null);
                setResaWarning('');
                setResaForm(emptyResaForm());
                setShowAddResa(!showAddResa);
              }}>
                {showAddResa ? '✕ Fermer' : '+ Ajouter une réservation'}
              </button>
            </div>

            {showAddResa && resaForm && (
              <div className="adm-carte adm-carte-pad resa-carte-edit qresa-carte" style={{ marginBottom: 16 }}>
                <strong className="resa-edit-titre">Réservation express</strong>
                {renderQuickResaForm()}
                {resaWarning && <p className="adm-aide" style={{ color: 'var(--rouge, #b3362c)' }}>{resaWarning}</p>}
                <div className="adm-actions qresa-actions">
                  <button className="adm-btn adm-btn-principal adm-btn-lg" onClick={createAdminResa}>
                    ✓ Enregistrer
                  </button>
                  <button className="adm-btn adm-btn-discret" onClick={() => { setShowAddResa(false); setResaForm(null); setShowResaDetails(false); }}>Annuler</button>
                </div>
              </div>
            )}

            {upcomingServices.length === 0 ? (
              <p className="adm-vide">Aucune réservation à venir</p>
            ) : (
              upcomingServices.map(renderServiceGroup)
            )}

            {pastCount > 0 && (
              <section className="adm-carte passe-bloc">
                <header className="passe-tete">
                  <div>
                    <strong>Services passés non traités ({pastCount})</strong>
                    <p>Marquez-les honorées pour garder une liste propre.</p>
                  </div>
                  <button className="adm-btn adm-btn-discret" onClick={() => setShowPastServices(!showPastServices)}>
                    {showPastServices ? 'Masquer' : 'Afficher'}
                  </button>
                </header>
                {showPastServices && pastServices.map(group => (
                  <div key={`${group.dateKey}|${group.service}`} className="passe-groupe">
                    <button className="adm-btn adm-btn-ok" onClick={() => completeServiceReservations(group)}>
                      ✓ Tout marquer honoré — {formatDayLabel(group.dateKey)} {SERVICE_META[group.service].label}
                    </button>
                    {renderServiceGroup(group)}
                  </div>
                ))}
              </section>
            )}


            <h2 className="adm-section-titre">Historique récent</h2>
            {completedReservations.length === 0 ? (
              <p className="adm-vide">Aucune réservation passée</p>
            ) : (
              <div className="adm-carte adm-liste">
                {completedReservations.slice(0, 20).map(r => (
                  <div key={r.id} className="adm-liste-ligne">
                    <span>
                      #{r.id} — {r.customerName} · {new Date(r.date).toLocaleDateString('fr-FR')} ·
                      {r.service === 'lunch' ? ' Midi' : ' Soir'} · {r.guests} pers.
                    </span>
                    {getStatusBadge(r.status)}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ===================== CARTE ===================== */}
        {activeTab === 'products' && (
          <>
            <div className="produits-tete">
              <h2 className="adm-section-titre" style={{ margin: 0, flex: 1 }}>La carte ({products.length})</h2>
              <button
                className="adm-btn adm-btn-principal"
                onClick={() => setShowAddProduct(v => !v)}
              >
                {showAddProduct ? '✕ Fermer' : '+ Nouveau produit'}
              </button>
            </div>

            {showAddProduct && (
              <form className="adm-carte adm-carte-pad produit-form" onSubmit={(e) => { handleAddProduct(e); }}>
                <div className="adm-champ">
                  <label>Nom</label>
                  <input type="text" value={productForm.name} required
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} />
                </div>
                <div className="adm-champ">
                  <label>Prix (€)</label>
                  <input type="number" step="0.01" value={productForm.price} required
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} />
                </div>
                <div className="adm-champ">
                  <label>Catégorie</label>
                  <input type="text" value={productForm.category} placeholder="Burgers, Plats…" required
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} />
                </div>
                <div className="adm-champ">
                  <label>Prix en points <span className="champ-option">(fidélité)</span></label>
                  <input type="number" min="1" value={productForm.points_price} placeholder="ex : 45"
                    onChange={(e) => setProductForm({ ...productForm, points_price: e.target.value })} />
                </div>
                <div className="adm-champ produit-desc-champ">
                  <label>Description</label>
                  <input type="text" value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} />
                </div>
                <button type="submit" className="adm-btn adm-btn-principal">Ajouter</button>
              </form>
            )}

            <p className="adm-aide">
              Astuce fidélité : le <strong>prix en points</strong> rend un produit offrable contre
              des points (repère ≈ prix × 10). Clique sur <strong>Modifier</strong> pour changer prix,
              nom, description ou points.
            </p>

            <div className="adm-carte adm-liste">
              {products.map(product => (
                <div key={product.id} className={`produit-bloc ${!product.available ? 'produit-off' : ''}`}>
                  <div className="produit-ligne2">
                    <div className="produit-ligne2-infos">
                      <strong>{product.name}</strong>
                      <span className="produit-ligne-meta">
                        {product.category} · {parseFloat(product.price).toFixed(2)}€
                        {product.points_price ? <span className="produit-pts-chip">⭐ {product.points_price} pts</span> : null}
                      </span>
                    </div>
                    <div className="produit-ligne2-actions">
                      <button
                        className={`adm-toggle ${product.available ? 'on' : 'off'}`}
                        onClick={() => toggleProductAvailability(product.id)}
                        title="Disponibilité"
                      >
                        {product.available ? '● En stock' : '○ Rupture'}
                      </button>
                      <button
                        className="adm-btn adm-btn-discret"
                        onClick={() => editingProductId === product.id ? setEditingProductId(null) : startEditProduct(product)}
                      >
                        {editingProductId === product.id ? 'Fermer' : 'Modifier'}
                      </button>
                    </div>
                  </div>

                  {editingProductId === product.id && editProductForm && (
                    <div className="produit-edit">
                      <div className="produit-edit-grille">
                        <div className="adm-champ">
                          <label>Nom</label>
                          <input type="text" value={editProductForm.name}
                            onChange={(e) => setEditProductForm({ ...editProductForm, name: e.target.value })} />
                        </div>
                        <div className="adm-champ">
                          <label>Prix (€)</label>
                          <input type="number" step="0.01" value={editProductForm.price}
                            onChange={(e) => setEditProductForm({ ...editProductForm, price: e.target.value })} />
                        </div>
                        <div className="adm-champ">
                          <label>Catégorie</label>
                          <input type="text" value={editProductForm.category}
                            onChange={(e) => setEditProductForm({ ...editProductForm, category: e.target.value })} />
                        </div>
                        <div className="adm-champ">
                          <label>Prix en points <span className="champ-option">(vide = non offrable)</span></label>
                          <input type="number" min="1" value={editProductForm.points_price} placeholder="—"
                            onChange={(e) => setEditProductForm({ ...editProductForm, points_price: e.target.value })} />
                        </div>
                        <div className="adm-champ produit-desc-champ">
                          <label>Description</label>
                          <input type="text" value={editProductForm.description}
                            onChange={(e) => setEditProductForm({ ...editProductForm, description: e.target.value })} />
                        </div>
                      </div>

                      <div className="adm-champ" style={{ marginTop: 12 }}>
                        <label>
                          Options / variantes <span className="champ-option">(sirop, picon… supplément en € — proposées à table et en prise de commande)</span>
                        </label>
                        {editProductForm.options.map((o, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                            <input type="text" placeholder='Nom (ex: "Picon", "Sirop pêche")' value={o.name}
                              style={{ flex: 2 }}
                              onChange={(e) => setEditProductForm({
                                ...editProductForm,
                                options: editProductForm.options.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x)
                              })} />
                            <input type="number" step="0.10" min="0" placeholder="+€" value={o.price}
                              style={{ width: 90 }}
                              onChange={(e) => setEditProductForm({
                                ...editProductForm,
                                options: editProductForm.options.map((x, xi) => xi === i ? { ...x, price: e.target.value } : x)
                              })} />
                            <button className="adm-btn adm-btn-mauvais" onClick={() => setEditProductForm({
                              ...editProductForm,
                              options: editProductForm.options.filter((_, xi) => xi !== i)
                            })}>✕</button>
                          </div>
                        ))}
                        <button
                          className="adm-btn adm-btn-discret"
                          onClick={() => setEditProductForm({
                            ...editProductForm,
                            options: [...editProductForm.options, { name: '', price: '' }]
                          })}
                        >
                          + Ajouter une option
                        </button>
                      </div>
                      <div className="produit-edit-pied">
                        <label className="produit-edit-toggle">
                          <span className="adm-switch">
                            <input type="checkbox" checked={product.can_take_away}
                              onChange={() => toggleProductCanTakeAway(product.id)} />
                            <span />
                          </span>
                          Disponible à emporter / click & collect
                        </label>
                        <div className="produit-edit-btns">
                          <button className="adm-btn adm-btn-mauvais" onClick={() => { deleteProduct(product.id); setEditingProductId(null); }}>
                            Supprimer le produit
                          </button>
                          <button className="adm-btn adm-btn-principal" onClick={saveEditProduct}>
                            Enregistrer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ===================== HORAIRES ===================== */}
        {/* ===================== STATISTIQUES ===================== */}
        {activeTab === 'stats' && (
          <>
            <div className="stats-tete">
              <h2 className="adm-section-titre" style={{ margin: 0, flex: 1 }}>Statistiques</h2>
              <div className="stats-filtres">
                {[
                  { k: 'week', l: '7 j' },
                  { k: 'month', l: '30 j' },
                  { k: 'quarter', l: '3 mois' },
                  { k: 'semester', l: '6 mois' },
                  { k: 'year', l: '1 an' },
                  { k: 'all', l: 'Tout' }
                ].map(p => (
                  <button
                    key={p.k}
                    className={`stats-filtre ${statsPeriod === p.k ? 'actif' : ''}`}
                    onClick={() => { setStatsPeriod(p.k); setStats(null); fetchStats(p.k); }}
                  >
                    {p.l}
                  </button>
                ))}
              </div>
            </div>

            {!stats ? (
              <p className="adm-vide">Chargement des statistiques…</p>
            ) : (
              <>
                <div className="stats-kpis">
                  <div className="kpi kpi-or">
                    <span className="kpi-val">{stats.revenue.toFixed(0)}€</span>
                    <span className="kpi-lbl">CA · {stats.periodLabel}</span>
                  </div>
                  <div className="kpi">
                    <span className="kpi-val">{stats.orders}</span>
                    <span className="kpi-lbl">Commandes · {stats.periodLabel}</span>
                  </div>
                  <div className="kpi">
                    <span className="kpi-val">{stats.avgBasket.toFixed(2)}€</span>
                    <span className="kpi-lbl">Panier moyen</span>
                  </div>
                  <div className="kpi">
                    <span className="kpi-val">{stats.activeOrders}</span>
                    <span className="kpi-lbl">Commandes en cours</span>
                  </div>
                </div>

                <div className="stats-kpis stats-kpis-sec">
                  <div className="kpi kpi-mini">
                    <span className="kpi-val">{stats.revenueAllTime.toFixed(0)}€</span>
                    <span className="kpi-lbl">CA total (tout temps)</span>
                  </div>
                  <div className="kpi kpi-mini">
                    <span className="kpi-val">{stats.reservations.upcoming}</span>
                    <span className="kpi-lbl">Résas à venir · {stats.reservations.upcomingCovers} couverts</span>
                  </div>
                  <div className="kpi kpi-mini">
                    <span className="kpi-val">{stats.loyalty.customers}</span>
                    <span className="kpi-lbl">Clients fidélité</span>
                  </div>
                  <div className="kpi kpi-mini">
                    <span className="kpi-val">{stats.loyalty.points_outstanding}</span>
                    <span className="kpi-lbl">Points en circulation</span>
                  </div>
                </div>

                <h2 className="adm-section-titre">Chiffre d'affaires · {stats.periodLabel}</h2>
                <div className="adm-carte adm-carte-pad">
                  {(() => {
                    const max = Math.max(1, ...stats.series.map(d => d.revenue));
                    const fmtLabel = (label) => stats.granularity === 'month'
                      ? new Date(`${label}-01T12:00:00`).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })
                      : `${label.slice(8)}/${label.slice(5, 7)}`;
                    return (
                      <div className={`stats-chart ${stats.series.length > 20 ? 'stats-chart-dense' : ''}`}>
                        {stats.series.map(d => (
                          <div key={d.label} className="chart-col" title={`${fmtLabel(d.label)} · ${d.revenue.toFixed(2)}€ · ${d.orders} cmd`}>
                            <div className="chart-bar-wrap">
                              <div className="chart-bar" style={{ height: `${Math.round((d.revenue / max) * 100)}%` }} />
                            </div>
                            <span className="chart-lbl">{fmtLabel(d.label)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                <div className="stats-deux-col">
                  <div>
                    <h2 className="adm-section-titre">Top produits · {stats.periodLabel}</h2>
                    <div className="adm-carte adm-liste">
                      {stats.topProducts.length === 0 ? (
                        <div className="adm-liste-ligne"><span>Aucune vente sur la période</span></div>
                      ) : stats.topProducts.map((p, i) => (
                        <div key={i} className="adm-liste-ligne">
                          <span><strong>{i + 1}.</strong> {p.name}</span>
                          <span className="adm-liste-fin">
                            <span className="adm-badge badge-fini">{p.qty} vendus</span>
                            <strong>{p.revenue.toFixed(2)}€</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h2 className="adm-section-titre">Réservations · {stats.periodLabel}</h2>
                    <div className="adm-carte adm-carte-pad stats-split">
                      <div className="split-item">
                        <span className="split-emoji">🌅</span>
                        <div>
                          <strong>{stats.reservations.split.lunch.n} services midi</strong>
                          <p>{stats.reservations.split.lunch.covers} couverts</p>
                        </div>
                      </div>
                      <div className="split-item">
                        <span className="split-emoji">🌙</span>
                        <div>
                          <strong>{stats.reservations.split.dinner.n} services soir</strong>
                          <p>{stats.reservations.split.dinner.covers} couverts</p>
                        </div>
                      </div>
                    </div>

                    <h2 className="adm-section-titre">Fidélité</h2>
                    <div className="adm-carte adm-carte-pad stats-fid">
                      <div><strong>{stats.loyalty.points_outstanding}</strong> points en circulation</div>
                      <div><strong>{stats.loyalty.points_redeemed}</strong> points déjà échangés</div>
                      {stats.peakHours.length > 0 && (
                        <div className="stats-peak">
                          Heures de retrait les plus fréquentes :
                          {' '}{stats.peakHours.map(h => `${h.h}h`).join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ===================== HISTORIQUE ===================== */}
        {activeTab === 'history' && (
          <>
            <div className="hist-switch">
              <button className={`hist-tab ${histType === 'orders' ? 'actif' : ''}`}
                onClick={() => { setHistType('orders'); setHistPage(1); setHistFilters({ q: '', status: 'all', channelOrService: 'all', from: '', to: '' }); }}>
                🧾 Commandes
              </button>
              <button className={`hist-tab ${histType === 'reservations' ? 'actif' : ''}`}
                onClick={() => { setHistType('reservations'); setHistPage(1); setHistFilters({ q: '', status: 'all', channelOrService: 'all', from: '', to: '' }); }}>
                📅 Réservations
              </button>
            </div>

            <div className="adm-carte adm-carte-pad hist-filtres">
              <div className="hist-recherche-champ">
                <span className="hist-recherche-loupe">🔎</span>
                <input type="search" className="hist-recherche" placeholder="Rechercher un nom, un téléphone ou un #numéro…"
                  value={histFilters.q}
                  onChange={(e) => { setHistFilters({ ...histFilters, q: e.target.value }); setHistPage(1); }} />
                {histFilters.q && (
                  <button className="hist-recherche-clear" onClick={() => { setHistFilters({ ...histFilters, q: '' }); setHistPage(1); }} aria-label="Effacer">✕</button>
                )}
              </div>
              <div className="hist-filtres-grille">
                <div className="adm-champ">
                  <label>Statut</label>
                  <select value={histFilters.status}
                    onChange={(e) => { setHistFilters({ ...histFilters, status: e.target.value }); setHistPage(1); }}>
                    <option value="all">Tous les statuts</option>
                    {histType === 'orders' ? (
                      <>
                        <option value="pending">En attente</option>
                        <option value="preparing">En préparation</option>
                        <option value="ready">Prête</option>
                        <option value="completed">Terminée / récupérée</option>
                        <option value="cancelled">Annulée</option>
                      </>
                    ) : (
                      <>
                        <option value="confirmed">Confirmée</option>
                        <option value="completed">Honorée</option>
                        <option value="cancelled">Annulée</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="adm-champ">
                  <label>{histType === 'orders' ? 'Canal' : 'Service'}</label>
                  <select value={histFilters.channelOrService}
                    onChange={(e) => { setHistFilters({ ...histFilters, channelOrService: e.target.value }); setHistPage(1); }}>
                    {histType === 'orders' ? (
                      <>
                        <option value="all">Tous les canaux</option>
                        <option value="pickup">À emporter</option>
                        <option value="dine_in">Sur place</option>
                      </>
                    ) : (
                      <>
                        <option value="all">Midi & soir</option>
                        <option value="lunch">Midi</option>
                        <option value="dinner">Soir</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="adm-champ">
                  <label>À partir du</label>
                  <input type="date" value={histFilters.from}
                    onChange={(e) => { setHistFilters({ ...histFilters, from: e.target.value }); setHistPage(1); }} />
                </div>
                <div className="adm-champ">
                  <label>Jusqu'au</label>
                  <input type="date" value={histFilters.to}
                    onChange={(e) => { setHistFilters({ ...histFilters, to: e.target.value }); setHistPage(1); }} />
                </div>
                {(histFilters.status !== 'all' || histFilters.channelOrService !== 'all' || histFilters.from || histFilters.to) && (
                  <button className="adm-btn adm-btn-discret hist-reset"
                    onClick={() => { setHistFilters({ ...histFilters, status: 'all', channelOrService: 'all', from: '', to: '' }); setHistPage(1); }}>
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            </div>

            <p className="adm-aide">{histLoading ? 'Chargement…' : `${histData.total} résultat${histData.total > 1 ? 's' : ''}`}</p>

            {histType === 'orders' ? (
              <div className="hist-liste">
                {histData.rows.map(o => (
                  <div key={o.id} className="adm-carte hist-ligne">
                    <div className="hist-ligne-tete">
                      <strong>#{o.id} · {o.customerName}</strong>
                      <span className="hist-ligne-meta">
                        {o.channel === 'dine_in' ? `🍽️ Table ${o.tableName || '?'}` : '🥡 À emporter'}
                        {' · '}{formatParisDateTime(o.createdAt)}
                      </span>
                    </div>
                    <div className="hist-ligne-articles">
                      {(o.items || []).filter(Boolean).map((it, i) => (
                        <span key={i} className="hist-chip">{it.quantity}× {it.name}</span>
                      ))}
                    </div>
                    <div className="hist-ligne-fin">
                      {o.customerPhone && <a href={`tel:${o.customerPhone}`}>{o.customerPhone}</a>}
                      <span className="hist-ligne-total">{parseFloat(o.total).toFixed(2)}€</span>
                      {getStatusBadge(o.status)}
                    </div>
                  </div>
                ))}
                {!histLoading && histData.rows.length === 0 && <p className="adm-vide">Aucun résultat</p>}
              </div>
            ) : (
              <div className="hist-liste">
                {histData.rows.map(r => (
                  <div key={r.id} className="adm-carte hist-ligne">
                    <div className="hist-ligne-tete">
                      <strong>#{r.id} · {r.customerName}</strong>
                      <span className="hist-ligne-meta">
                        {new Date(`${String(r.date).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                        {' · '}{r.service === 'lunch' ? 'midi' : 'soir'}
                        {r.time ? ` · ${r.time.slice(0, 5)}` : ''}
                      </span>
                    </div>
                    {r.notes && <p className="hist-ligne-notes">💬 {r.notes}</p>}
                    <div className="hist-ligne-fin">
                      {r.customerPhone && <a href={`tel:${r.customerPhone}`}>{r.customerPhone}</a>}
                      <span className="hist-ligne-total">{r.guests} pers.</span>
                      <span className={`adm-badge ${r.status === 'cancelled' ? 'badge-annule' : r.status === 'completed' ? 'badge-fini' : 'badge-confirme'}`}>
                        {r.status === 'cancelled' ? 'Annulée' : r.status === 'completed' ? 'Honorée' : 'Confirmée'}
                      </span>
                    </div>
                  </div>
                ))}
                {!histLoading && histData.rows.length === 0 && <p className="adm-vide">Aucun résultat</p>}
              </div>
            )}

            {histData.totalPages > 1 && (
              <div className="hist-pagination">
                <button className="adm-btn adm-btn-discret" disabled={histData.page <= 1}
                  onClick={() => setHistPage(histData.page - 1)}>← Précédent</button>
                <span>Page {histData.page} / {histData.totalPages}</span>
                <button className="adm-btn adm-btn-discret" disabled={histData.page >= histData.totalPages}
                  onClick={() => setHistPage(histData.page + 1)}>Suivant →</button>
              </div>
            )}
          </>
        )}

        {/* ===================== PARAMÈTRES ===================== */}
        {activeTab === 'settings' && (
          <>
            <h2 className="adm-section-titre">Écrans de service</h2>
            <div className="adm-carte adm-carte-pad">
              <p className="adm-aide">
                Trois écrans dédiés au service sur place, à ouvrir sur n'importe quel
                appareil (tablette murale en cuisine, tablette au bar, téléphone des
                serveurs). Connectez chaque tablette une fois avec votre mot de
                passe : elle reste connectée et prête à l'emploi.
              </p>
              <div className="adm-actions">
                <a className="adm-btn adm-btn-principal" href="/cuisine" target="_blank" rel="noreferrer">👨‍🍳 Écran cuisine</a>
                <a className="adm-btn adm-btn-principal" href="/caisse" target="_blank" rel="noreferrer">💶 Caisse</a>
                <a className="adm-btn adm-btn-principal" href="/service" target="_blank" rel="noreferrer">🧑‍💼 Mode serveur</a>
              </div>
              <p className="adm-aide" style={{ marginTop: 10 }}>
                Astuce tablette : ouvrez l'écran voulu puis « Ajouter à l'écran
                d'accueil » pour l'avoir en plein écran comme une application.
              </p>
            </div>

            <h2 className="adm-section-titre">Salle & QR codes des tables</h2>
            <div className="adm-carte adm-carte-pad">
              <p className="adm-aide">
                Déclarez vos tables : chacune reçoit un QR code unique à imprimer et
                poser dessus. Les clients scannent, commandent, et tout part en
                cuisine — l'addition de la table se remplit toute seule.
              </p>

              <div className="adm-actions" style={{ marginBottom: 14 }}>
                <input
                  type="text"
                  placeholder='Nom de la table (ex: "1", "Terrasse 2")'
                  value={newTableName}
                  onChange={(e) => setNewTableName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addDiningTable()}
                  style={{ flex: 1, minWidth: 180 }}
                />
                <button className="adm-btn adm-btn-principal" onClick={addDiningTable} disabled={!newTableName.trim()}>
                  + Ajouter la table
                </button>
                {diningTables.some(t => t.active) && (
                  <button className="adm-btn adm-btn-ok" onClick={printQrCodes}>
                    🖨 Imprimer les QR codes
                  </button>
                )}
              </div>

              {diningTables.length === 0 ? (
                <p className="adm-vide">Aucune table déclarée pour le moment.</p>
              ) : (
                <div className="adm-liste">
                  {diningTables.map(t => (
                    <div key={t.id} className="adm-liste-ligne">
                      <span>
                        <strong>Table {t.name}</strong>
                        {!t.active && <span className="adm-badge badge-annule" style={{ marginLeft: 8 }}>désactivée</span>}
                      </span>
                      <span className="adm-liste-fin">
                        <button className="adm-btn adm-btn-discret" onClick={() => regenerateTableQr(t)} title="Invalide l'ancien QR imprimé">
                          ↻ Nouveau QR
                        </button>
                        <button className="adm-btn adm-btn-discret" onClick={() => toggleDiningTable(t)}>
                          {t.active ? 'Désactiver' : 'Réactiver'}
                        </button>
                        <button className="adm-btn adm-btn-mauvais" onClick={() => removeDiningTable(t)}>✕</button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <h2 className="adm-section-titre">Notifications sur cet appareil</h2>
            <div className="adm-carte adm-carte-pad">
              <p className="adm-aide">
                Recevez une notification à chaque nouvelle commande ou réservation,
                même quand l'écran est éteint. Sur iPhone/iPad : ajoutez d'abord le
                site à l'écran d'accueil (Partager → Sur l'écran d'accueil), puis
                activez depuis l'application installée.
              </p>
              <div className="adm-actions">
                {pushThisDevice ? (
                  <>
                    <span className="adm-badge badge-pret">✓ Activées sur cet appareil</span>
                    <button className="adm-btn adm-btn-discret" onClick={() => authFetch('/push/test', { method: 'POST' })}>
                      Envoyer un test
                    </button>
                    <button className="adm-btn adm-btn-mauvais" onClick={disablePush}>Désactiver</button>
                  </>
                ) : (
                  <button className="adm-btn adm-btn-principal" onClick={enablePush}>
                    🔔 Activer les notifications
                  </button>
                )}
                {pushStatus && pushStatus.enabled && (
                  <span className="adm-aide" style={{ margin: 0 }}>
                    {pushStatus.devices} appareil{pushStatus.devices > 1 ? 's' : ''} abonné{pushStatus.devices > 1 ? 's' : ''} au total
                  </span>
                )}
                {pushStatus && !pushStatus.enabled && (
                  <span className="adm-badge badge-annule">Non configurées côté serveur</span>
                )}
              </div>
            </div>

            <h2 className="adm-section-titre">Fermetures exceptionnelles</h2>
            <div className="adm-carte adm-carte-pad">
              <p className="adm-aide">
                Congés, jours fériés, privatisation… Pendant une fermeture, le site
                refuse les commandes et/ou les réservations sur la période, avec votre
                message affiché au client.
              </p>
              <div className="capa-form">
                <div className="adm-champ">
                  <label>Du</label>
                  <DatePicker
                    selected={closureForm.startDate ? new Date(closureForm.startDate) : null}
                    onChange={(d) => setClosureForm({ ...closureForm, startDate: dateToKey(d) || null })}
                    dateFormat="dd/MM/yyyy"
                    locale="fr"
                    placeholderText="Début"
                  />
                </div>
                <div className="adm-champ">
                  <label>Au</label>
                  <DatePicker
                    selected={closureForm.endDate ? new Date(closureForm.endDate) : null}
                    onChange={(d) => setClosureForm({ ...closureForm, endDate: dateToKey(d) || null })}
                    minDate={closureForm.startDate ? new Date(closureForm.startDate) : null}
                    dateFormat="dd/MM/yyyy"
                    locale="fr"
                    placeholderText="Fin"
                  />
                </div>
                <div className="adm-champ">
                  <label>Bloque</label>
                  <select
                    value={closureForm.scope}
                    onChange={(e) => setClosureForm({ ...closureForm, scope: e.target.value })}
                  >
                    <option value="all">Tout</option>
                    <option value="orders">Commandes</option>
                    <option value="reservations">Réservations</option>
                  </select>
                </div>
                <div className="adm-champ capa-note">
                  <label>Message (optionnel)</label>
                  <input
                    type="text"
                    value={closureForm.note}
                    placeholder="Ex : congés annuels, retour le 15 août !"
                    onChange={(e) => setClosureForm({ ...closureForm, note: e.target.value })}
                  />
                </div>
                <button className="adm-btn adm-btn-principal" onClick={addClosure}>Ajouter</button>
              </div>

              {closures.length > 0 && (
                <div className="adm-liste" style={{ marginTop: 16 }}>
                  {closures.map(c => (
                    <div key={c.id} className="adm-liste-ligne">
                      <span>
                        {new Date(c.startDate).toLocaleDateString('fr-FR')} → {new Date(c.endDate).toLocaleDateString('fr-FR')}
                        {' '}<span className={`adm-badge ${c.scope === 'all' ? 'badge-annule' : c.scope === 'orders' ? 'badge-prepa' : 'badge-confirme'}`}>
                          {c.scope === 'all' ? 'Tout fermé' : c.scope === 'orders' ? 'Commandes' : 'Réservations'}
                        </span>
                        {c.note ? ` · ${c.note}` : ''}
                      </span>
                      <button className="adm-btn adm-btn-mauvais" onClick={() => removeClosure(c.id)}>Supprimer</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <h2 className="adm-section-titre">Rythme de la cuisine</h2>
            <div className="adm-carte adm-carte-pad">
              <div className="capa-defaut">
                <div className="adm-champ">
                  <label>Commandes max par créneau de 15 min (0 = illimité)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={appSettings.maxOrdersPerSlot}
                    onChange={(e) => setAppSettings({ maxOrdersPerSlot: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <button className="adm-btn adm-btn-principal" onClick={saveAppSettings}>Enregistrer</button>
              </div>
              <p className="adm-aide" style={{ marginTop: 10, marginBottom: 0 }}>
                Quand un créneau atteint la limite, il disparaît du choix des clients —
                les commandes se répartissent sur les créneaux voisins.
              </p>
            </div>

            <h2 className="adm-section-titre">Horaires d'ouverture</h2>
            {Object.keys(timeSlots).length === 0 ? (
              <p className="adm-vide">Chargement…</p>
            ) : (
              <div className="horaires-grille">
                {Object.entries(timeSlots).map(([day, slot]) => (
                  <div key={day} className={`adm-carte adm-carte-pad horaire-carte ${!slot.enabled ? 'horaire-ferme' : ''}`}>
                    <div className="horaire-tete">
                      <strong>{DAY_LABELS[day] || day}</strong>
                      <label className="adm-switch">
                        <input
                          type="checkbox"
                          checked={slot.enabled}
                          onChange={(e) => updateTimeSlot(day, 'enabled', e.target.checked)}
                        />
                        <span />
                      </label>
                    </div>

                    {slot.enabled ? (
                      <>
                        <div className="horaire-tranche">
                          <input
                            type="time"
                            value={slot.open}
                            onChange={(e) => updateTimeSlot(day, 'open', e.target.value)}
                          />
                          <span>→</span>
                          <input
                            type="time"
                            value={slot.close}
                            onChange={(e) => updateTimeSlot(day, 'close', e.target.value)}
                          />
                        </div>

                        <label className="horaire-2e">
                          <input
                            type="checkbox"
                            checked={slot.enabled2 || false}
                            onChange={(e) => updateTimeSlot(day, 'enabled2', e.target.checked)}
                          />
                          2ᵉ tranche
                        </label>

                        {slot.enabled2 && (
                          <div className="horaire-tranche">
                            <input
                              type="time"
                              value={slot.open2 || ''}
                              onChange={(e) => updateTimeSlot(day, 'open2', e.target.value)}
                            />
                            <span>→</span>
                            <input
                              type="time"
                              value={slot.close2 || ''}
                              onChange={(e) => updateTimeSlot(day, 'close2', e.target.value)}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="horaire-ferme-txt">Fermé</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <h2 className="adm-section-titre">Mon parc de tables</h2>
            <div className="adm-carte adm-carte-pad">
              <p className="adm-aide">
                Déclarez vos tables réelles : le système calcule la capacité de chaque
                service et vérifie qu'une vraie disposition existe avant d'accepter une
                réservation (les tables <strong>modulables</strong> peuvent s'accoler
                entre elles, ex. 4 + 2 pour une tablée de 6).
              </p>

              <form className="parc-form" onSubmit={addTableType}>
                <div className="adm-champ">
                  <label>Nombre</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={typeForm.count}
                    onChange={(e) => setTypeForm({ ...typeForm, count: parseInt(e.target.value) || 1 })}
                    required
                  />
                </div>
                <div className="adm-champ">
                  <label>Places par table</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={typeForm.seats}
                    onChange={(e) => setTypeForm({ ...typeForm, seats: parseInt(e.target.value) || 2 })}
                    required
                  />
                </div>
                <label className="parc-modulable">
                  <input
                    type="checkbox"
                    checked={typeForm.modulable}
                    onChange={(e) => setTypeForm({ ...typeForm, modulable: e.target.checked })}
                  />
                  Modulable (peut s'accoler)
                </label>
                <div className="adm-champ">
                  <label>Étiquette (optionnel)</label>
                  <input
                    type="text"
                    value={typeForm.label}
                    placeholder="Ex : tourets, terrasse…"
                    onChange={(e) => setTypeForm({ ...typeForm, label: e.target.value })}
                  />
                </div>
                <button type="submit" className="adm-btn adm-btn-principal">Ajouter</button>
              </form>

              {tableTypes.types.length > 0 ? (
                <>
                  <div className="adm-liste parc-liste">
                    {tableTypes.types.map(type => (
                      <div key={type.id} className="adm-liste-ligne parc-ligne">
                        <div className="parc-ligne-infos">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            className="parc-count"
                            value={type.count}
                            onChange={(e) => {
                              const count = parseInt(e.target.value) || 0;
                              setTableTypes(prev => ({
                                ...prev,
                                types: prev.types.map(t => t.id === type.id ? { ...t, count } : t)
                              }));
                            }}
                            onBlur={(e) => saveTableType({ ...type, count: parseInt(e.target.value) || 0 })}
                          />
                          <span className="parc-desc">
                            × table de <strong>{type.seats}</strong>
                            {type.label && <em> ({type.label})</em>}
                          </span>
                          {type.modulable
                            ? <span className="adm-badge badge-pret">Modulable</span>
                            : <span className="adm-badge badge-fini">Fixe</span>}
                        </div>
                        <button className="adm-btn adm-btn-mauvais" onClick={() => deleteTableType(type.id)}>
                          Supprimer
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="parc-total">
                    Total : <strong>{tableTypes.totalTables} tables · {tableTypes.totalSeats} couverts</strong> par service
                  </p>
                </>
              ) : (
                <div className="parc-fallback">
                  <p className="adm-aide">
                    Aucune table déclarée : le système utilise pour l'instant la capacité
                    simple ci-dessous (un simple total de couverts, sans vérifier la disposition).
                  </p>
                  <div className="capa-defaut">
                    <div className="adm-champ">
                      <label>Capacité par défaut (couverts par service)</label>
                      <input
                        type="number"
                        min="1"
                        max="500"
                        value={tablesConfig.tables ? tablesConfig.tables.reduce((s, t) => s + t.capacity, 0) : 30}
                        onChange={(e) => setTablesConfig({
                          totalTables: 1,
                          tables: [{ id: 1, capacity: parseInt(e.target.value) || 0 }]
                        })}
                      />
                    </div>
                    <button
                      className="adm-btn adm-btn-principal"
                      onClick={async () => {
                        try {
                          await authFetch('/tables/config', {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(tablesConfig)
                          });
                          alert('Capacité mise à jour !');
                        } catch (e) { console.error(e); }
                      }}
                    >
                      Enregistrer
                    </button>
                  </div>
                </div>
              )}

              <hr className="adm-sep" />

              <h3 className="adm-sous-titre">Capacités exceptionnelles</h3>
              <p className="adm-aide">
                Plafond de couverts différent pour une date donnée (soirée spéciale, effectif réduit…).
                {tableTypes.types.length > 0 && ' Avec le parc de tables actif, ce plafond s\'ajoute à la vérification des tables — il ne peut pas en inventer.'}
              </p>

              <div className="capa-form">
                <div className="adm-champ">
                  <label>Date</label>
                  <DatePicker
                    selected={newOverride.date ? new Date(newOverride.date) : null}
                    onChange={(date) => setNewOverride({ ...newOverride, date: dateToKey(date) || null })}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    locale="fr"
                    placeholderText="Choisir…"
                  />
                </div>
                <div className="adm-champ">
                  <label>Service</label>
                  <select
                    value={newOverride.service}
                    onChange={(e) => setNewOverride({ ...newOverride, service: e.target.value })}
                  >
                    <option value="both">Midi + Soir</option>
                    <option value="lunch">Midi</option>
                    <option value="dinner">Soir</option>
                  </select>
                </div>
                <div className="adm-champ">
                  <label>Capacité</label>
                  <input
                    type="number"
                    min="1"
                    value={newOverride.capacity}
                    onChange={(e) => setNewOverride({ ...newOverride, capacity: e.target.value })}
                  />
                </div>
                <div className="adm-champ capa-note">
                  <label>Note</label>
                  <input
                    type="text"
                    value={newOverride.note}
                    placeholder="Ex : concert, tables en terrasse…"
                    onChange={(e) => setNewOverride({ ...newOverride, note: e.target.value })}
                  />
                </div>
                <button className="adm-btn adm-btn-principal" onClick={addCapacityOverride}>Ajouter</button>
              </div>

              {capacityOverrides.length > 0 && (
                <div className="adm-liste" style={{ marginTop: 16 }}>
                  {capacityOverrides.map(o => (
                    <div key={o.id} className="adm-liste-ligne">
                      <span>
                        {new Date(o.override_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                        {' '}<span className={`adm-badge ${o.service === 'lunch' ? 'badge-prepa' : o.service === 'dinner' ? 'badge-confirme' : 'badge-fini'}`}>
                          {o.service === 'lunch' ? 'Midi' : o.service === 'dinner' ? 'Soir' : 'Midi + Soir'}
                        </span>
                        {' '}· {o.capacity} places{o.note ? ` · ${o.note}` : ''}
                      </span>
                      <button className="adm-btn adm-btn-mauvais" onClick={() => deleteCapacityOverride(o.id)}>Supprimer</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ===================== FIDÉLITÉ ===================== */}
        {activeTab === 'loyalty' && (
          <>
            <h2 className="adm-section-titre">Programme de fidélité</h2>
            {!loyaltySettings ? (
              <p className="adm-vide">Chargement…</p>
            ) : (
              <div className="adm-carte adm-carte-pad">
                <label className="adm-switch-ligne">
                  <label className="adm-switch">
                    <input
                      type="checkbox"
                      checked={loyaltySettings.enabled}
                      onChange={(e) => setLoyaltySettings({ ...loyaltySettings, enabled: e.target.checked })}
                    />
                    <span />
                  </label>
                  Programme {loyaltySettings.enabled ? 'activé' : 'désactivé'}
                </label>

                <div className="fid-reglages">
                  <div className="adm-champ">
                    <label>Points par euro dépensé</label>
                    <input
                      type="number"
                      min="1"
                      value={loyaltySettings.pointsPerEuro}
                      onChange={(e) => setLoyaltySettings({ ...loyaltySettings, pointsPerEuro: e.target.value })}
                    />
                  </div>
                  <div className="adm-champ">
                    <label>Bonus de bienvenue (points)</label>
                    <input
                      type="number"
                      min="0"
                      value={loyaltySettings.welcomePoints ?? 0}
                      onChange={(e) => setLoyaltySettings({ ...loyaltySettings, welcomePoints: e.target.value })}
                    />
                  </div>
                </div>

                <p className="adm-aide">
                  En clair : 1€ dépensé = {loyaltySettings.pointsPerEuro} point{loyaltySettings.pointsPerEuro > 1 ? 's' : ''} (crédités au retrait de la commande).
                  Les clients échangent ensuite leurs points contre des produits offerts — tu fixes le
                  <strong> prix en points de chaque produit</strong> dans l'onglet Carte.
                  {parseInt(loyaltySettings.welcomePoints) > 0 ? ` ${loyaltySettings.welcomePoints} points sont offerts à l'inscription.` : ''}
                </p>

                <div className="adm-actions">
                  <button className="adm-btn adm-btn-principal" onClick={saveLoyaltySettings}>Enregistrer</button>
                  {loyaltySaved && <span className="fid-sauve">✓ Enregistré</span>}
                </div>
              </div>
            )}

            <h2 className="adm-section-titre">🔥 Soirées fidélité (points multipliés)</h2>
            <div className="adm-carte adm-carte-pad">
              <p className="adm-aide">
                Créez une période où les points sont multipliés — « Soirée double points »
                affichée automatiquement sur le site.
              </p>

              <form className="event-form" onSubmit={createLoyaltyEvent}>
                <div className="adm-champ event-nom">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={eventForm.name}
                    placeholder="Ex : Soirée double points"
                    onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="adm-champ">
                  <label>Multiplicateur</label>
                  <select
                    value={eventForm.multiplier}
                    onChange={(e) => setEventForm({ ...eventForm, multiplier: parseFloat(e.target.value) })}
                  >
                    {[1.5, 2, 3, 4, 5].map(m => <option key={m} value={m}>x{m}</option>)}
                  </select>
                </div>
                <div className="adm-champ">
                  <label>Du</label>
                  <DatePicker
                    selected={eventForm.startDate ? new Date(eventForm.startDate) : null}
                    onChange={(date) => setEventForm({ ...eventForm, startDate: dateToKey(date) || null })}
                    dateFormat="dd/MM/yyyy"
                    locale="fr"
                    placeholderText="Début"
                  />
                </div>
                <div className="adm-champ">
                  <label>Au</label>
                  <DatePicker
                    selected={eventForm.endDate ? new Date(eventForm.endDate) : null}
                    onChange={(date) => setEventForm({ ...eventForm, endDate: dateToKey(date) || null })}
                    minDate={eventForm.startDate ? new Date(eventForm.startDate) : null}
                    dateFormat="dd/MM/yyyy"
                    locale="fr"
                    placeholderText="Fin"
                  />
                </div>
                <div className="adm-champ">
                  <label>Service</label>
                  <select
                    value={eventForm.service}
                    onChange={(e) => setEventForm({ ...eventForm, service: e.target.value })}
                  >
                    <option value="all">Midi + Soir</option>
                    <option value="lunch">Midi</option>
                    <option value="dinner">Soir</option>
                  </select>
                </div>
                <button type="submit" className="adm-btn adm-btn-principal">Créer</button>
              </form>

              {loyaltyEvents.length > 0 && (
                <div className="adm-liste" style={{ marginTop: 18 }}>
                  {loyaltyEvents.map(event => (
                    <div key={event.id} className={`adm-liste-ligne ${!event.active ? 'event-off' : ''}`}>
                      <span>
                        <span className="adm-badge badge-or">x{parseFloat(event.multiplier)}</span>
                        {' '}<strong>{event.name}</strong>
                        {' '}· {new Date(event.startDate).toLocaleDateString('fr-FR')} → {new Date(event.endDate).toLocaleDateString('fr-FR')}
                        {' '}· {event.service === 'lunch' ? 'Midi' : event.service === 'dinner' ? 'Soir' : 'Midi + Soir'}
                        {!event.active && ' · (désactivée)'}
                      </span>
                      <span className="adm-liste-fin">
                        <button className="adm-btn adm-btn-discret" onClick={() => toggleLoyaltyEvent(event.id)}>
                          {event.active ? 'Désactiver' : 'Réactiver'}
                        </button>
                        <button className="adm-btn adm-btn-mauvais" onClick={() => deleteLoyaltyEvent(event.id)}>
                          Supprimer
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <h2 className="adm-section-titre">Clients inscrits ({loyaltyCustomers.length})</h2>
            {loyaltyCustomers.length === 0 ? (
              <p className="adm-vide">Aucun client inscrit pour le moment</p>
            ) : (
              <>
                <div className="adm-actions" style={{ marginBottom: 12 }}>
                  <button
                    className="adm-btn adm-btn-discret"
                    onClick={() => {
                      const emails = loyaltyCustomers
                        .filter(c => c.email && c.marketingOptin)
                        .map(c => c.email);
                      if (emails.length === 0) {
                        alert('Aucun client n\'a accepté de recevoir les promos pour le moment.');
                        return;
                      }
                      navigator.clipboard.writeText(emails.join(', '))
                        .then(() => alert(`${emails.length} email(s) copié(s) — collez-les dans votre logiciel de mail (en Cci !).`))
                        .catch(() => alert(emails.join(', ')));
                    }}
                  >
                    📋 Copier les emails promo ({loyaltyCustomers.filter(c => c.email && c.marketingOptin).length})
                  </button>
                </div>
                <div className="adm-carte fid-table-scroll">
                  <table className="fid-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Téléphone</th>
                        <th>Email</th>
                        <th>Promos</th>
                        <th>Points</th>
                        <th>Commandes</th>
                        <th>Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loyaltyCustomers.map(c => (
                        <React.Fragment key={c.id}>
                          <tr className={managedCustomerId === c.id ? 'fid-row-open' : ''}>
                            <td><strong>{c.name}</strong></td>
                            <td>{c.phone.replace(/(\d{2})(?=\d)/g, '$1 ')}</td>
                            <td>{c.email || '—'}</td>
                            <td>{c.marketingOptin ? <span className="adm-badge badge-pret">Oui</span> : <span className="adm-badge badge-fini">Non</span>}</td>
                            <td><span className="adm-badge badge-or">{c.balance}</span></td>
                            <td>{c.completedOrders}</td>
                            <td>{parseFloat(c.totalSpent).toFixed(2)}€</td>
                            <td>
                              <button className="adm-btn adm-btn-discret" onClick={() => openCustomer(c)}>
                                {managedCustomerId === c.id ? 'Fermer' : 'Gérer'}
                              </button>
                            </td>
                          </tr>
                          {managedCustomerId === c.id && custEdit && (
                            <tr className="fid-panel-row">
                              <td colSpan={8}>
                                <div className="fid-panel">
                                  {custMsg && <div className="fid-panel-msg">{custMsg}</div>}
                                  <div className="fid-panel-grille">
                                    {/* Points */}
                                    <div className="fid-panel-bloc">
                                      <h4>Points de fidélité</h4>
                                      <p className="fid-panel-solde">Solde actuel : <strong>{c.balance} pts</strong></p>
                                      <div className="fid-panel-champs">
                                        <input type="number" min="1" placeholder="Nb de points"
                                          value={pointsAdjust.points}
                                          onChange={(e) => setPointsAdjust({ ...pointsAdjust, points: e.target.value })} />
                                        <input type="text" placeholder="Motif (ex : geste commercial)"
                                          value={pointsAdjust.label}
                                          onChange={(e) => setPointsAdjust({ ...pointsAdjust, label: e.target.value })} />
                                      </div>
                                      <div className="fid-panel-btns">
                                        <button className="adm-btn adm-btn-ok" onClick={() => adjustCustomerPoints(1)}>+ Créditer</button>
                                        <button className="adm-btn adm-btn-discret" onClick={() => adjustCustomerPoints(-1)}>− Débiter</button>
                                      </div>
                                    </div>

                                    {/* Infos */}
                                    <div className="fid-panel-bloc">
                                      <h4>Informations</h4>
                                      <div className="fid-panel-champs">
                                        <input type="text" placeholder="Nom" value={custEdit.name}
                                          onChange={(e) => setCustEdit({ ...custEdit, name: e.target.value })} />
                                        <input type="tel" placeholder="Téléphone" value={custEdit.phone}
                                          onChange={(e) => setCustEdit({ ...custEdit, phone: formatPhoneFR(e.target.value) })} />
                                        <input type="email" placeholder="Email" value={custEdit.email}
                                          onChange={(e) => setCustEdit({ ...custEdit, email: e.target.value })} />
                                        <label className="fid-panel-optin">
                                          <input type="checkbox" checked={custEdit.marketingOptin}
                                            onChange={(e) => setCustEdit({ ...custEdit, marketingOptin: e.target.checked })} />
                                          Accepte les promos
                                        </label>
                                      </div>
                                      <div className="fid-panel-btns">
                                        <button className="adm-btn adm-btn-principal" onClick={saveCustomer}>Enregistrer</button>
                                      </div>
                                    </div>

                                    {/* Sécurité */}
                                    <div className="fid-panel-bloc">
                                      <h4>Compte</h4>
                                      <p className="adm-aide" style={{ margin: '0 0 10px' }}>
                                        Mot de passe oublié ? Réinitialise-le pour le client.
                                      </p>
                                      <div className="fid-panel-btns">
                                        <button className="adm-btn adm-btn-discret" onClick={resetCustomerPassword}>
                                          🔑 Réinitialiser le mot de passe
                                        </button>
                                        <button className="adm-btn adm-btn-mauvais" onClick={() => deleteCustomer(c)}>
                                          Supprimer le compte
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default Admin;
